import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { track } from "@/lib/track";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import { cityCoords } from "@/lib/places";
import { useLocation } from "@/context/LocationContext";
import { ListingCard } from "@/components/ListingCard";
import { Mascot } from "@/components/Mascot";

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const { loc } = useLocation();
  const pt = loc.coords || cityCoords(loc.country, loc.city);
  const [q, setQ] = useState(sp.get("q") || "");
  const [cat, setCat] = useState("");
  const [items, setItems] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = (query, category) => {
    setLoading(true);
    if (query) track("search", { q: query.slice(0, 80) });
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (category) params.set("category", category);
    if (pt) { params.set("lat", pt.lat); params.set("lng", pt.lng); params.set("sort", "distance"); }
    Promise.all([
      api.get(`/listings?${params.toString()}`),
      query ? api.get(`/community/posts?q=${encodeURIComponent(query)}`) : Promise.resolve({ data: [] }),
    ]).then(([l, p]) => { setItems(l.data.items); setPosts(p.data); }).finally(() => setLoading(false));
  };

  useEffect(() => { run(sp.get("q") || "", cat); }, [pt?.lat, pt?.lng]); // eslint-disable-line

  const submit = (e) => { e.preventDefault(); setSp(q ? { q } : {}); run(q, cat); };

  return (
    <div className="gs-fade px-4 pt-4">
      <form onSubmit={submit}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kërko gjithçka..." className="w-full bg-white rounded-2xl pl-12 pr-4 h-14 shadow-sm border border-slate-100 focus:border-emerald-400 outline-none" />
        </div>
      </form>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3 pb-1">
        <button data-testid="search-cat-all" onClick={() => { setCat(""); run(q, ""); }} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${!cat ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>Të gjitha</button>
        {CATEGORIES.filter((c) => !c.route).map((c) => (
          <button key={c.key} data-testid={`search-cat-${c.key}`} onClick={() => { setCat(c.key); run(q, c.key); }} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${cat === c.key ? `${c.solid} text-white border-transparent` : "bg-white text-slate-600 border-slate-200"}`}>{c.label}</button>
        ))}
      </div>

      {loading ? <p className="text-center text-slate-400 py-10">Duke kërkuar...</p> : (
        <>
          <p className="text-sm text-slate-400 mt-3">{items.length + posts.length} rezultate</p>
          <div className="grid grid-cols-2 gap-3 mt-2">{items.map((l) => <ListingCard key={l.id} listing={l} />)}</div>
          {posts.length > 0 && (
            <div className="mt-5">
              <h3 className="font-bold text-slate-800 mb-2">Nga komunitetet</h3>
              <div className="space-y-2">
                {posts.map((p) => (
                  <a key={p.id} href={`/komunitete/${p.id}`} className="block bg-white rounded-2xl p-3 border border-slate-100">
                    <span className="text-xs text-cyan-600 font-semibold">{p.topic}</span>
                    <div className="font-semibold text-sm text-slate-800">{p.title}</div>
                  </a>
                ))}
              </div>
            </div>
          )}
          {!items.length && !posts.length && <Mascot testid="search-empty-mascot" title="Nuk u gjet asgjë" text="Provo fjalë të tjera." />}
        </>
      )}
    </div>
  );
}
